// ABOUTME: Renders a native React clarification card from resolve_ambiguity tool output.
// ABOUTME: Multiple-choice options + open-ended text input. Supports paginated multi-question.

import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, HelpCircle, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { ClarificationCardData, ClarificationOption } from '@/types/sdk';

interface QuestionPage {
  question: string;
  options: ClarificationOption[];
}

interface ClarificationCardProps {
  cardData: ClarificationCardData;
  investigationId: string;
  dataSourceId?: string;
  onSendFollowUp?: (message: string) => void;
  /** If provided, the card renders in resolved state (e.g., loaded from history). */
  initialResolvedLabel?: string;
}

export function ClarificationCard({
  cardData,
  investigationId: _investigationId,
  dataSourceId: _dataSourceId,
  onSendFollowUp,
  initialResolvedLabel,
}: ClarificationCardProps) {
  // Build list of all question pages
  const pages: QuestionPage[] = [
    { question: cardData.question, options: cardData.options ?? [] },
    ...(cardData.additional_questions ?? []).map((aq) => ({
      question: aq.question,
      options: (aq.options ?? []) as ClarificationOption[],
    })),
  ];
  const totalPages = pages.length;
  const isMultiPage = totalPages > 1;

  const [currentPage, setCurrentPage] = useState(0);
  // Per-page answers — null means unanswered
  const [answers, setAnswers] = useState<(string | null)[]>(
    () => new Array(totalPages).fill(null)
  );
  const [isResolved, setIsResolved] = useState(!!initialResolvedLabel);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(initialResolvedLabel ?? null);
  const [customInput, setCustomInput] = useState('');

  const allAnswered = answers.every((a) => a !== null);

  const handleSelectOption = useCallback(
    (label: string) => {
      if (isResolved) return;

      // Store answer for current page
      const newAnswers = [...answers];
      newAnswers[currentPage] = label;
      setAnswers(newAnswers);
      setCustomInput('');

      // Single-page: resolve immediately
      if (!isMultiPage) {
        setIsResolved(true);
        setSelectedLabel(label);
        if (onSendFollowUp) {
          onSendFollowUp(label);
        }
        return;
      }

      // Multi-page: auto-advance to next unanswered page (or stay if all done)
      const allNowAnswered = newAnswers.every((a) => a !== null);
      if (!allNowAnswered) {
        // Find next unanswered page
        for (let i = 1; i <= totalPages; i++) {
          const nextIdx = (currentPage + i) % totalPages;
          if (newAnswers[nextIdx] === null) {
            setCurrentPage(nextIdx);
            return;
          }
        }
      }
      // All answered — stay on current page, user can submit
    },
    [isResolved, onSendFollowUp, answers, currentPage, totalPages, isMultiPage]
  );

  const handleCustomSubmit = useCallback(() => {
    const trimmed = customInput.trim();
    if (!trimmed || isResolved) return;
    handleSelectOption(trimmed);
  }, [customInput, isResolved, handleSelectOption]);

  const handleSubmitAll = useCallback(() => {
    if (!allAnswered || isResolved) return;
    const combined = answers.filter(Boolean).join('; ');
    setIsResolved(true);
    setSelectedLabel(combined);
    if (onSendFollowUp) {
      onSendFollowUp(combined);
    }
  }, [allAnswered, isResolved, answers, onSendFollowUp]);

  const page = pages[currentPage];
  const currentAnswer = answers[currentPage];
  const normalizedOptions = (page?.options ?? []).map((rawOpt, idx) => {
    if (typeof rawOpt === 'string') {
      return { id: `option_${idx + 1}`, label: rawOpt, description: '' };
    }
    return rawOpt;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-sm overflow-hidden border transition-all duration-300 ${
        isResolved
          ? 'border-success/30 bg-success-light/10'
          : 'border-primary-200 bg-surface-0'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-surface-100">
        {isResolved ? (
          <CheckCircle className="w-4 h-4 text-success" />
        ) : (
          <HelpCircle className="w-4 h-4 text-primary-500" />
        )}
        <span className="text-sm font-medium text-surface-700">
          {isResolved ? `Resolved: ${selectedLabel}` : 'Clarification needed'}
        </span>
        {isMultiPage && !isResolved && (
          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="p-0.5 text-surface-400 hover:text-surface-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-surface-400">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="p-0.5 text-surface-400 hover:text-surface-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
            className="space-y-3"
          >
            {/* Question — supports markdown (bold, italic, code, tables) */}
            <div className="text-sm text-surface-800 [&>p:first-child]:font-semibold [&_strong]:font-bold [&_em]:italic [&_code]:bg-surface-100 [&_code]:px-1 [&_code]:rounded [&_code]:font-mono [&_code]:text-xs [&_table]:w-full [&_table]:text-left [&_table]:border-collapse [&_table]:mt-2 [&_table]:mb-2 [&_th]:px-2 [&_th]:py-1 [&_th]:border-b [&_th]:border-surface-200 [&_th]:text-xs [&_th]:font-medium [&_th]:text-surface-500 [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-surface-100 [&_td]:text-xs [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-0.5 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:space-y-0.5">
              <ReactMarkdown>{page?.question ?? ''}</ReactMarkdown>
            </div>

            {/* Options */}
            {normalizedOptions.length > 0 && (
              <div className="space-y-2">
                {normalizedOptions.map((opt, idx) => {
                  const isRecommended = opt.id === cardData.recommendation;
                  const isSelected = currentAnswer === opt.label;

                  return (
                    <button
                      key={opt.id || `opt-${idx}`}
                      onClick={() => handleSelectOption(opt.label)}
                      disabled={isResolved}
                      className={`w-full text-left px-3 py-2.5 rounded-sm border transition-all duration-150 ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : isResolved
                            ? 'border-surface-200 opacity-40'
                            : isRecommended
                              ? 'border-primary-400 bg-primary-50 hover:border-primary-500 cursor-pointer'
                              : 'border-surface-200 hover:border-primary-400 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-surface-800">{opt.label}</span>
                        {isRecommended && !isResolved && !isSelected && (
                          <span className="text-[10px] font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-sm">
                            recommended
                          </span>
                        )}
                        {isSelected && (
                          <CheckCircle className="w-3.5 h-3.5 text-primary-500 ml-auto" />
                        )}
                      </div>
                      {opt.description && (
                        <p className="text-xs text-surface-500 mt-0.5">{opt.description}</p>
                      )}
                      {opt.sql_preview && (
                        <code className="block mt-1.5 px-2 py-1 bg-surface-50 rounded text-[11px] font-mono text-surface-600 whitespace-pre-wrap">
                          {opt.sql_preview}
                        </code>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Open-ended input */}
            {!isResolved && (
              <div className="pt-1">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCustomSubmit();
                      }
                    }}
                    placeholder="Or type your own answer..."
                    className="flex-1 text-sm px-3 py-2 border border-surface-200 rounded-sm bg-surface-0 text-surface-800 placeholder-surface-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <button
                    onClick={handleCustomSubmit}
                    disabled={!customInput.trim()}
                    className="p-2 rounded-sm text-surface-400 hover:text-primary-500 hover:bg-primary-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    title="Send custom answer"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Dot pagination — clickable */}
        {isMultiPage && !isResolved && (
          <div className="flex items-center justify-center gap-1.5 pt-1">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                  i === currentPage
                    ? 'bg-primary-500'
                    : answers[i] !== null
                      ? 'bg-success'
                      : 'bg-surface-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* Submit all — only shown when multi-page and all answered */}
        {isMultiPage && allAnswered && !isResolved && (
          <button
            onClick={handleSubmitAll}
            className="w-full py-2 px-3 rounded-sm bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
          >
            Submit answers
          </button>
        )}

        {/* Footer */}
        <p className="text-[11px] text-surface-400 pt-1">
          I'll remember this for your workspace
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Try to parse a tool result as clarification card data.
 * Returns the parsed data if successful, null otherwise.
 */
export function parseClarificationCardData(
  toolResultContent: string | Record<string, unknown>[] | null
): ClarificationCardData | null {
  if (!toolResultContent) return null;

  let text = '';
  if (typeof toolResultContent === 'string') {
    text = toolResultContent;
  } else if (Array.isArray(toolResultContent)) {
    // SDK content block format: [{type: 'text', text: '...'}]
    const textBlock = toolResultContent.find(
      (item): item is { type: string; text: string } =>
        typeof item === 'object' && item !== null && (item as any).type === 'text'
    );
    if (textBlock) {
      text = (textBlock as any).text;
    }
  }

  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    if (parsed && parsed.type === 'clarification_card' && parsed.question) {
      return parsed as ClarificationCardData;
    }
  } catch {
    // Not JSON or not a clarification card
  }

  return null;
}
